import React, { useState } from 'react';
import { cn } from '@/lib/utils';
import type { GrammarIssue } from '@/lib/grammar';

interface GrammarOverlayProps {
    text: string;
    issues: GrammarIssue[];
    style: React.CSSProperties;
    className?: string;
    onApplySuggestion?: (issue: GrammarIssue, suggestion: string) => void;
}

export function GrammarOverlay({ text, issues, style, className, onApplySuggestion }: GrammarOverlayProps) {
    const [selectedIssue, setSelectedIssue] = useState<GrammarIssue | null>(null);
    const [popupPos, setPopupPos] = useState<{ x: number, y: number } | null>(null);

    if (!issues || issues.length === 0) return null;

    // Sort issues by start position
    const sortedIssues = [...issues].sort((a, b) => a.start - b.start);

    const parts: React.ReactNode[] = [];
    let lastIndex = 0;

    const handleIssueClick = (e: React.MouseEvent, issue: GrammarIssue) => {
        e.preventDefault();
        e.stopPropagation();
        setSelectedIssue(issue);
        setPopupPos({ x: e.clientX, y: e.clientY });
    };

    sortedIssues.forEach((issue, idx) => {
        // Add text before issue
        if (issue.start > lastIndex) {
            parts.push(text.slice(lastIndex, issue.start));
        }

        // Add highlighted issue
        parts.push(
            <span
                key={`${issue.start}-${idx}`}
                onClick={(e) => handleIssueClick(e, issue)}
                className={cn(
                    "cursor-pointer pointer-events-auto",
                    issue.type === 'spelling' ? 'grammar-highlight-spelling' :
                        issue.type === 'grammar' ? 'grammar-highlight-grammar' :
                            'grammar-highlight-style'
                )}
            >
                {text.slice(issue.start, issue.end)}
            </span>
        );
        lastIndex = issue.end;
    });

    // Add remaining text
    if (lastIndex < text.length) {
        parts.push(text.slice(lastIndex));
    }

    return (
        <>
            <div
                className={cn("absolute inset-0 pointer-events-none whitespace-pre-wrap break-words overflow-hidden", className)}
                style={{ ...style, color: 'transparent', zIndex: 0 }}
            >
                {parts}
            </div>

            {selectedIssue && popupPos && (
                <div
                    className="fixed z-[100] bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-lg shadow-2xl p-3 w-64 animate-in fade-in zoom-in duration-200"
                    style={{ left: Math.min(window.innerWidth - 260, popupPos.x), top: popupPos.y + 10 }}
                >
                    <div className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest mb-1">
                        {selectedIssue.type === 'spelling' ? 'Spelling Correct' : 'Suggestion'}
                    </div>
                    <div className="text-sm font-medium mb-3 italic">"{selectedIssue.message}"</div>

                    <div className="flex flex-wrap gap-2">
                        {selectedIssue.suggestions?.map((s, i) => (
                            <button
                                key={i}
                                onClick={() => {
                                    onApplySuggestion?.(selectedIssue, s);
                                    setSelectedIssue(null);
                                }}
                                className="px-3 py-1 bg-amber-500/10 hover:bg-amber-500/20 text-amber-600 dark:text-amber-400 text-xs rounded-full transition-colors border border-amber-500/20"
                            >
                                {s}
                            </button>
                        ))}
                    </div>

                    <button
                        onClick={() => setSelectedIssue(null)}
                        className="mt-4 text-[10px] text-zinc-400 hover:text-zinc-600 uppercase"
                    >
                        Dismiss
                    </button>
                </div>
            )}
        </>
    );
}
